// Whitelist for Arabic-only inputs: Arabic script blocks (letters, diacritics,
// Arabic-Indic digits, Arabic punctuation), Western digits, plain whitespace
// (space/tab/newline - deliberately not the \s class, which would re-admit
// the invisible U+FEFF) and neutral punctuation. Everything else - Latin letters included - is stripped
// as the user types or pastes. The presentation-forms range ends at U+FEFC so
// the invisible U+FEFF (BOM/ZWNBSP) cannot get in. Keep the regex in sync with
// api.fitfind.com/src/services/arabicInput.service.js.
const NON_ARABIC_CHARS =
  /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFC0-9\t\n\r .,:;!?()\-_'"«»%&+/]/g;

export const sanitizeArabic = (value) =>
  typeof value === "string" ? value.replace(NON_ARABIC_CHARS, "") : value;

// Arabic fields follow the `...Ar` naming convention (nameAr, titleAr,
// marketplaceNameAr, ...). Generic form handlers use this to decide whether
// a field must be sanitized.
export const isArabicField = (name) => typeof name === "string" && /Ar$/.test(name);

export const sanitizeArabicField = (name, value) =>
  isArabicField(name) ? sanitizeArabic(value) : value;

// Caret-preserving variant for controlled inputs. When sanitization removes
// characters, React re-renders with a value the DOM did not expect and the
// browser drops the caret to the end of the field. Restore it to where it
// logically belongs: the number of characters BEFORE the old caret that
// survive sanitization.
export const sanitizeArabicEvent = (e) => {
  const el = e.target;
  const raw = el.value;
  const clean = sanitizeArabic(raw);
  if (clean !== raw && typeof el.setSelectionRange === "function") {
    const caret = sanitizeArabic(raw.slice(0, el.selectionStart ?? raw.length)).length;
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(caret, caret);
      } catch {
        // input unmounted or no longer selectable - nothing to restore
      }
    });
  }
  return clean;
};

// Generic-handler variant: caret-safe sanitize only when the changed field
// follows the `...Ar` naming convention; other fields pass through raw.
export const sanitizeArabicFieldEvent = (e) =>
  isArabicField(e.target?.name) ? sanitizeArabicEvent(e) : e.target.value;
